import { useEffect, useRef, useState } from 'react'

// Dependency-free SVG line over time (one point per day or week) — the line sibling of
// RevenueChart, for counts. One axis for both series (same kind of number: a count a day).
//   data   [{ key, label, full, [line.key]: n, [bar.key]: n }] — label under the axis,
//          full in the tooltip title
//   line   { key, label, color, unit } — a gently curved 2px line (monotone, so it never
//          dips below 0 or overshoots a peak) over a faint area
//   bar    { key, label, color, unit } — optional thin columns under the line for a
//          sparse count (นัดได้), each non-zero one labelled with its number
// With a bar there are two series, so a legend sits underneath. Hover (or touch)
// anywhere over the plot: a crosshair, the point, a tooltip beside it.

const fmt = (n) => Math.round(Number(n) || 0).toLocaleString('en-US')

// integer ticks for small counts: 0 … yMax in steps of 1 · 2 · 5 · 10 …
function countScale(peak) {
  if (peak <= 0) return { yMax: 4, step: 1 }
  const raw = peak / 4
  const pow = Math.pow(10, Math.floor(Math.log10(raw)))
  const step = Math.max(1, [1, 2, 5, 10].map((m) => m * pow).find((s) => s >= raw) || 10 * pow)
  return { yMax: Math.ceil(peak / step) * step, step }
}

// A monotone cubic through the points (Fritsch–Carlson, as d3's curveMonotoneX): flat at
// every peak and trough, so a day with 0 calls stays on the baseline.
function monotonePath(pts) {
  const n = pts.length
  if (n < 3) return pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x},${y}`).join('')
  const h = [], m = []
  for (let i = 0; i < n - 1; i++) { h[i] = pts[i + 1][0] - pts[i][0]; m[i] = (pts[i + 1][1] - pts[i][1]) / h[i] }
  const t = [m[0]]
  for (let i = 1; i < n - 1; i++) {
    const p = (m[i - 1] * h[i] + m[i] * h[i - 1]) / (h[i - 1] + h[i])
    t[i] = (Math.sign(m[i - 1]) + Math.sign(m[i])) * Math.min(Math.abs(m[i - 1]), Math.abs(m[i]), 0.5 * Math.abs(p)) || 0
  }
  t[n - 1] = m[n - 2]
  let d = `M${pts[0][0]},${pts[0][1]}`
  for (let i = 0; i < n - 1; i++) {
    const [x0, y0] = pts[i], [x1, y1] = pts[i + 1], k = h[i] / 3
    d += `C${x0 + k},${y0 + k * t[i]} ${x1 - k},${y1 - k * t[i + 1]} ${x1},${y1}`
  }
  return d
}

export default function TrendLine({ data = [], line = { key: 'value', label: '', color: '#2563eb', unit: '' }, bar, height = 200, emptyText = 'ไม่มีข้อมูล' }) {
  const wrapRef = useRef(null)
  const [width, setWidth] = useState(720)
  const [hover, setHover] = useState(null)
  const empty = !data.length

  // re-attach when the box swaps between the empty note and the chart
  useEffect(() => {
    const el = wrapRef.current
    if (!el) return
    const fit = (w) => { if (w) setWidth(Math.max(280, Math.round(w))) }
    fit(el.getBoundingClientRect().width)
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver((entries) => fit(entries[0]?.contentRect?.width))
    ro.observe(el)
    return () => ro.disconnect()
  }, [empty])

  if (empty) {
    return <div ref={wrapRef} className="flex items-center justify-center text-[13px] text-slate-400" style={{ height }}>{emptyText}</div>
  }

  const M = { top: 14, right: 14, bottom: 26, left: 30 }
  const plotW = Math.max(10, width - M.left - M.right)
  const plotH = Math.max(10, height - M.top - M.bottom)
  const vals = data.map((d) => Number(d[line.key]) || 0)
  const bars = bar ? data.map((d) => Number(d[bar.key]) || 0) : []
  const { yMax, step } = countScale(Math.max(0, ...vals, ...bars))
  const ticks = Array.from({ length: Math.round(yMax / step) + 1 }, (_, i) => i * step)
  // columns need half a column of room at both ends; a bare line runs edge to edge
  const barW = bar ? Math.max(3, Math.min(12, (plotW / data.length) * 0.5)) : 0
  const pad = bar ? barW / 2 + 2 : 0
  const x = (i) => (data.length === 1 ? plotW / 2 : pad + ((plotW - 2 * pad) * i) / (data.length - 1))
  const y = (v) => plotH - (plotH * v) / yMax
  const path = monotonePath(vals.map((v, i) => [x(i), y(v)]))
  const area = `${path}L${x(vals.length - 1)},${plotH}L${x(0)},${plotH}Z`
  const labelStep = Math.max(1, Math.ceil(data.length / Math.max(2, Math.floor(plotW / 52))))

  // the pointer's x → the nearest point
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect()
    const px = ((e.clientX - r.left) / r.width) * width - M.left
    const i = data.length === 1 ? 0 : Math.round((px / plotW) * (data.length - 1))
    setHover(Math.max(0, Math.min(data.length - 1, i)))
  }

  const hv = hover != null ? data[hover] : null
  const hx = hv ? M.left + x(hover) : 0
  // beside the point, never on it: right of it on the left half, left of it after
  const tipLeft = !hv ? 0 : hx < width / 2 ? Math.min(width - 170, hx + 12) : Math.max(0, hx - 12 - 170)

  return (
    <div ref={wrapRef} className="relative w-full">
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} className="select-none touch-pan-y"
        onPointerMove={onMove} onPointerLeave={() => setHover(null)}>
        <g transform={`translate(${M.left},${M.top})`}>
          {ticks.map((t) => (
            <g key={t}>
              <line x1={0} y1={y(t)} x2={plotW} y2={y(t)} stroke="#eef2f6" strokeWidth={1} />
              <text x={-8} y={y(t)} dy="0.32em" textAnchor="end" className="fill-slate-400" fontSize={10.5}>{t}</text>
            </g>
          ))}
          {/* every labelStep-th day, and always the last — minus a regular label that would
              sit closer than one step to it (they collided: "28 ต.ค.30 ต.ค.") */}
          {data.map((d, i) => ((i % labelStep === 0 && (data.length - 1 - i >= labelStep || i === data.length - 1)) || i === data.length - 1) && (
            <text key={d.key ?? i} x={x(i)} y={plotH + 17} textAnchor={i === 0 ? 'start' : i === data.length - 1 ? 'end' : 'middle'}
              className="fill-slate-400" fontSize={10.5}>{d.label}</text>
          ))}
          <line x1={0} y1={plotH} x2={plotW} y2={plotH} stroke="#cbd5e1" strokeWidth={1} />

          {/* the columns sit under the line; square at the baseline, rounded at the top */}
          {bar && bars.map((v, i) => v > 0 && (
            <g key={`b${i}`}>
              <rect x={x(i) - barW / 2} y={y(v)} width={barW} height={plotH - y(v)} rx={Math.min(3, barW / 2)} fill={bar.color} />
              <rect x={x(i) - barW / 2} y={Math.max(y(v), plotH - 3)} width={barW} height={Math.min(3, plotH - y(v))} fill={bar.color} />
              <text x={x(i)} y={y(v) - 4} textAnchor="middle" className="fill-slate-600" fontSize={10.5} fontWeight={600}>{v}</text>
            </g>
          ))}

          <path d={area} fill={line.color} opacity={0.08} />
          <path d={path} fill="none" stroke={line.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />

          {hv && (
            <>
              <line x1={x(hover)} y1={0} x2={x(hover)} y2={plotH} stroke="#94a3b8" strokeWidth={1} strokeDasharray="3 3" />
              <circle cx={x(hover)} cy={y(vals[hover])} r={4.5} fill={line.color} stroke="#fff" strokeWidth={2} />
            </>
          )}
        </g>
      </svg>

      {hv && (
        <div className="pointer-events-none absolute top-1 z-10 w-[170px] rounded-lg bg-slate-900/95 px-3 py-2 text-[11px] text-white shadow-lg"
          style={{ left: tipLeft }}>
          <p className="mb-0.5 font-semibold">{hv.full || hv.label}</p>
          <p className="flex items-center gap-2">
            <span className="h-[3px] w-3 rounded-full" style={{ background: line.color }} />
            <span className="text-slate-300">{line.label}</span>
            <b className="tabular-nums">{fmt(hv[line.key])}</b>
            {line.unit && <span className="text-slate-300">{line.unit}</span>}
          </p>
          {bar && (
            <p className="flex items-center gap-2">
              <span className="h-2 w-3 rounded-sm" style={{ background: bar.color }} />
              <span className="text-slate-300">{bar.label}</span>
              <b className="tabular-nums">{fmt(hv[bar.key])}</b>
              {bar.unit && <span className="text-slate-300">{bar.unit}</span>}
            </p>
          )}
        </div>
      )}

      {bar && (
        <div className="mt-1 flex flex-wrap items-center justify-center gap-x-5 gap-y-1">
          <span className="inline-flex items-center gap-1.5 text-[12px] text-slate-600">
            <span className="h-[3px] w-3.5 rounded-full" style={{ background: line.color }} />{line.label}
          </span>
          <span className="inline-flex items-center gap-1.5 text-[12px] text-slate-600">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: bar.color }} />{bar.label}
          </span>
        </div>
      )}
    </div>
  )
}
